import { useState } from 'react';
import {
  View, Text, TouchableOpacity, TextInput,
  StyleSheet, ScrollView, ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/hooks/useAuth';
import { useOnboarding, PersonalData } from '@/hooks/useOnboarding';
import { OnboardingData } from '@/lib/types';

const GOALS = [
  { value: 'hypertrophy', label: '💪 Hipertrofia', desc: 'Ganhar massa muscular e ficar mais forte' },
  { value: 'weight_loss', label: '🔥 Emagrecimento', desc: 'Perder gordura e definir o corpo' },
  { value: 'conditioning', label: '🏃 Condicionamento', desc: 'Melhorar fôlego e resistência física' },
  { value: 'health', label: '❤️ Saúde geral', desc: 'Me manter ativo e saudável no dia a dia' },
] as const;

const LEVELS = [
  { value: 'beginner', label: '🌱 Iniciante', desc: 'Menos de 1 ano' },
  { value: 'intermediate', label: '⚡ Intermediário', desc: '1–3 anos' },
  { value: 'advanced', label: '🏆 Avançado', desc: 'Mais de 3 anos' },
] as const;

const DAYS = [2, 3, 4, 5] as const;

const MUSCLES = [
  { value: 'legs', label: '🦵 Pernas', desc: 'Glúteos, quadríceps, posterior' },
  { value: 'back', label: '🔙 Costas', desc: 'Dorsal, trapézio, lombar' },
  { value: 'chest', label: '💛 Peito', desc: 'Peitoral maior e menor' },
  { value: 'shoulders', label: '🔵 Ombros', desc: 'Deltoides anterior, lateral e posterior' },
  { value: 'arms', label: '💪 Bíceps / Tríceps', desc: 'Braços em geral' },
  { value: 'abs', label: '⬜ Abdômen', desc: 'Core e região central' },
] as const;

const SEX_OPTIONS = [
  { value: 'M' as const, label: 'Masculino' },
  { value: 'F' as const, label: 'Feminino' },
  { value: 'O' as const, label: 'Outro' },
];

type GoalValue = typeof GOALS[number]['value'];
type LevelValue = typeof LEVELS[number]['value'];

function formatDate(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function parseBirthDate(display: string): string | undefined {
  if (!display.trim()) return undefined;
  const parts = display.split('/');
  if (parts.length !== 3) return undefined;
  const [d, m, y] = parts;
  if (!d || !m || !y || y.length !== 4) return undefined;
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

// Steps: 1=personal data, 2=goal, 3=level, 4=days, 5=muscles, 6=limitations, 7=conclusion
export default function OnboardingScreen() {
  const { profile } = useAuth();
  const router = useRouter();

  const [step, setStep] = useState(1);

  // Step 1 — personal data
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [sex, setSex] = useState<'M' | 'F' | 'O' | null>(null);

  // Steps 2–6 — workout preferences
  const [goal, setGoal] = useState<GoalValue | null>(null);
  const [level, setLevel] = useState<LevelValue | null>(null);
  const [days, setDays] = useState<2 | 3 | 4 | 5 | null>(null);
  const [muscles, setMuscles] = useState<string[]>([]);
  const [limitations, setLimitations] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [step1Errors, setStep1Errors] = useState<Record<string, string>>({});

  function validateStep1(): boolean {
    const errs: Record<string, string> = {};
    if (!fullName.trim()) errs.fullName = 'Nome é obrigatório';
    if (phone.replace(/\D/g, '').length < 10) errs.phone = 'Telefone é obrigatório';
    if (!parseBirthDate(birthDate)) errs.birthDate = 'Data inválida (use DD/MM/AAAA)';
    if (!heightCm) errs.heightCm = 'Altura é obrigatória';
    if (!weightKg) errs.weightKg = 'Peso é obrigatório';
    if (!sex) errs.sex = 'Selecione o sexo biológico';
    setStep1Errors(errs);
    return Object.keys(errs).length === 0;
  }

  const { saveOnboarding, studentId, fetchError } = useOnboarding(profile?.id ?? '');

  function toggleMuscle(value: string) {
    setMuscles(prev =>
      prev.includes(value)
        ? prev.filter(m => m !== value)
        : prev.length < 3 ? [...prev, value] : prev
    );
  }

  async function handleConclude() {
    if (!goal || !level || !days || muscles.length === 0 || !studentId) return;
    setSaving(true);
    setSaveError(false);
    const personal: PersonalData = {
      full_name: fullName.trim(),
      phone: phone.trim() || undefined,
      birth_date: parseBirthDate(birthDate),
      height_cm: heightCm ? parseFloat(heightCm) : undefined,
      weight_kg: weightKg ? parseFloat(weightKg) : undefined,
      sex: sex ?? undefined,
    };
    const onboarding: OnboardingData = {
      goal,
      level,
      days_per_week: days,
      muscle_focus: muscles,
      limitations,
    };
    try {
      await saveOnboarding(onboarding, personal);
      setStep(7);
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  }

  const progress = Math.min(step, 6) / 6;

  if (fetchError) {
    return (
      <View style={styles.conclusionScreen}>
        <Text style={styles.conclusionTitle}>Erro ao carregar</Text>
        <Text style={styles.conclusionText}>
          Não foi possível carregar seus dados. Tente novamente.
        </Text>
      </View>
    );
  }

  if (step === 7) {
    return (
      <View style={styles.conclusionScreen}>
        <Text style={styles.conclusionEmoji}>🎉</Text>
        <Text style={styles.conclusionTitle}>Tudo pronto!</Text>
        <Text style={styles.conclusionText}>
          Seu personal foi notificado e em breve você terá um treino personalizado.
        </Text>
        <TouchableOpacity
          style={styles.conclusionBtn}
          onPress={() => router.replace('/(student)/waiting' as any)}
        >
          <Text style={styles.conclusionBtnText}>Ir para o app →</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <View style={styles.progressTrack}>
        <View style={[styles.progressFill, { width: `${progress * 100}%` }]} />
      </View>
      <Text style={styles.stepLabel}>{step} de 6</Text>

      <ScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
      >

        {step === 1 && (
          <>
            <Text style={styles.question}>Vamos te conhecer melhor!</Text>
            <Text style={styles.hint}>Preencha seus dados para personalizar sua experiência</Text>

            <Text style={styles.fieldLabel}>Nome completo *</Text>
            <TextInput
              style={[styles.fieldInput, step1Errors.fullName && styles.fieldInputError]}
              value={fullName}
              onChangeText={v => { setFullName(v); setStep1Errors(e => ({ ...e, fullName: '' })); }}
              placeholder="Seu nome completo"
              placeholderTextColor="#666"
              autoCapitalize="words"
            />
            {step1Errors.fullName ? <Text style={styles.fieldError}>{step1Errors.fullName}</Text> : null}

            <Text style={styles.fieldLabel}>Telefone *</Text>
            <TextInput
              style={[styles.fieldInput, step1Errors.phone && styles.fieldInputError]}
              value={phone}
              onChangeText={t => { setPhone(formatPhone(t)); setStep1Errors(e => ({ ...e, phone: '' })); }}
              placeholder="(11) 99999-9999"
              placeholderTextColor="#666"
              keyboardType="phone-pad"
              maxLength={16}
            />
            {step1Errors.phone ? <Text style={styles.fieldError}>{step1Errors.phone}</Text> : null}

            <Text style={styles.fieldLabel}>Data de nascimento *</Text>
            <TextInput
              style={[styles.fieldInput, step1Errors.birthDate && styles.fieldInputError]}
              value={birthDate}
              onChangeText={t => { setBirthDate(formatDate(t)); setStep1Errors(e => ({ ...e, birthDate: '' })); }}
              placeholder="DD/MM/AAAA"
              placeholderTextColor="#666"
              keyboardType="numeric"
              maxLength={10}
            />
            {step1Errors.birthDate ? <Text style={styles.fieldError}>{step1Errors.birthDate}</Text> : null}

            <Text style={styles.fieldLabel}>Altura (cm) *</Text>
            <TextInput
              style={[styles.fieldInput, step1Errors.heightCm && styles.fieldInputError]}
              value={heightCm}
              onChangeText={v => { setHeightCm(v); setStep1Errors(e => ({ ...e, heightCm: '' })); }}
              placeholder="175"
              placeholderTextColor="#666"
              keyboardType="numeric"
              maxLength={3}
            />
            {step1Errors.heightCm ? <Text style={styles.fieldError}>{step1Errors.heightCm}</Text> : null}

            <Text style={styles.fieldLabel}>Peso (kg) *</Text>
            <TextInput
              style={[styles.fieldInput, step1Errors.weightKg && styles.fieldInputError]}
              value={weightKg}
              onChangeText={v => { setWeightKg(v); setStep1Errors(e => ({ ...e, weightKg: '' })); }}
              placeholder="70"
              placeholderTextColor="#666"
              keyboardType="numeric"
              maxLength={5}
            />
            {step1Errors.weightKg ? <Text style={styles.fieldError}>{step1Errors.weightKg}</Text> : null}

            <Text style={styles.fieldLabel}>Sexo biológico *</Text>
            <View style={[styles.sexRow, step1Errors.sex && { marginBottom: 0 }]}>
              {SEX_OPTIONS.map(opt => (
                <TouchableOpacity
                  key={opt.value}
                  style={[styles.sexBtn, sex === opt.value && styles.sexBtnActive]}
                  onPress={() => { setSex(sex === opt.value ? null : opt.value); setStep1Errors(e => ({ ...e, sex: '' })); }}
                >
                  <Text style={[styles.sexBtnText, sex === opt.value && styles.sexBtnTextActive]}>
                    {opt.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {step1Errors.sex ? <Text style={styles.fieldError}>{step1Errors.sex}</Text> : null}
          </>
        )}

        {step === 2 && (
          <>
            <Text style={styles.question}>Qual é seu principal objetivo?</Text>
            {GOALS.map(g => (
              <TouchableOpacity
                key={g.value}
                style={[styles.card, goal === g.value && styles.cardSelected]}
                onPress={() => setGoal(g.value)}
              >
                <Text style={[styles.cardLabel, goal === g.value && styles.cardLabelSelected]}>
                  {g.label}
                </Text>
                <Text style={[styles.cardDesc, goal === g.value && styles.cardDescSelected]}>
                  {g.desc}
                </Text>
              </TouchableOpacity>
            ))}
          </>
        )}

        {step === 3 && (
          <>
            <Text style={styles.question}>Qual seu nível de experiência?</Text>
            {LEVELS.map(l => (
              <TouchableOpacity
                key={l.value}
                style={[styles.card, level === l.value && styles.cardSelected]}
                onPress={() => setLevel(l.value)}
              >
                <Text style={[styles.cardLabel, level === l.value && styles.cardLabelSelected]}>
                  {l.label}
                </Text>
                <Text style={[styles.cardDesc, level === l.value && styles.cardDescSelected]}>
                  {l.desc}
                </Text>
              </TouchableOpacity>
            ))}
          </>
        )}

        {step === 4 && (
          <>
            <Text style={styles.question}>Quantos dias por semana você consegue treinar?</Text>
            <Text style={styles.hint}>
              Seja honesto — menos dias regulares é melhor do que muitos sem cumprir
            </Text>
            <View style={styles.tilesRow}>
              {DAYS.map(d => (
                <TouchableOpacity
                  key={d}
                  style={[styles.tile, days === d && styles.tileSelected]}
                  onPress={() => setDays(d)}
                >
                  <Text style={[styles.tileNumber, days === d && styles.tileNumberSelected]}>
                    {d}
                  </Text>
                  <Text style={[styles.tileSub, days === d && styles.tileSubSelected]}>dias</Text>
                </TouchableOpacity>
              ))}
            </View>
          </>
        )}

        {step === 5 && (
          <>
            <Text style={styles.question}>O que você mais quer trabalhar?</Text>
            <Text style={styles.hint}>Escolha até 3 · Seu personal vai priorizar esses grupos</Text>
            {MUSCLES.map(m => {
              const selected = muscles.includes(m.value);
              return (
                <TouchableOpacity
                  key={m.value}
                  style={[styles.muscleCard, selected && styles.muscleCardSelected]}
                  onPress={() => toggleMuscle(m.value)}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.cardLabel, selected && styles.cardLabelSelected]}>
                      {m.label}
                    </Text>
                    <Text style={[styles.cardDesc, selected && styles.cardDescSelected]}>
                      {m.desc}
                    </Text>
                  </View>
                  {selected && <Text style={styles.checkMark}>✓</Text>}
                </TouchableOpacity>
              );
            })}
          </>
        )}

        {step === 6 && (
          <>
            <Text style={styles.question}>Tem alguma limitação física?</Text>
            <Text style={styles.hint}>
              Lesões, dores ou restrições que seu personal precisa saber para montar um treino
              seguro para você
            </Text>
            <TextInput
              style={styles.textarea}
              value={limitations}
              onChangeText={setLimitations}
              placeholder="Ex: dor no joelho direito ao agachar, hérnia lombar L4-L5…"
              placeholderTextColor="#666"
              multiline
              numberOfLines={5}
              textAlignVertical="top"
            />
            <Text style={styles.optional}>Pode pular se não tiver nada a informar</Text>
          </>
        )}

      </ScrollView>

      <View style={styles.footer}>
        {saveError && (
          <Text style={styles.saveErrorText}>Erro ao salvar. Tente novamente.</Text>
        )}
        {step < 6 ? (
          <TouchableOpacity
            style={[
              styles.nextBtn,
              (step > 1 && !canAdvance(step, fullName, goal, level, days, muscles)) && styles.nextBtnDisabled,
            ]}
            onPress={() => {
              if (step === 1) { if (!validateStep1()) return; }
              else if (!canAdvance(step, fullName, goal, level, days, muscles)) return;
              setStep(s => s + 1);
            }}
          >
            <Text style={styles.nextBtnText}>Próximo →</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={[styles.nextBtn, saving && styles.nextBtnDisabled]}
            onPress={handleConclude}
            disabled={saving}
          >
            {saving
              ? <ActivityIndicator color="#fff" />
              : <Text style={styles.nextBtnText}>Concluir →</Text>
            }
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

function canAdvance(
  step: number,
  fullName: string,
  goal: string | null,
  level: string | null,
  days: number | null,
  muscles: string[],
): boolean {
  if (step === 1) return fullName.trim().length > 0;
  if (step === 2) return goal !== null;
  if (step === 3) return level !== null;
  if (step === 4) return days !== null;
  if (step === 5) return muscles.length > 0;
  return true;
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#1a1a1a' },
  progressTrack: {
    height: 6, backgroundColor: '#333', marginHorizontal: 16, marginTop: 16, borderRadius: 8,
  },
  progressFill: { height: '100%', backgroundColor: '#C8102E', borderRadius: 8 },
  stepLabel: { fontSize: 11, color: '#aaa', textAlign: 'center', marginTop: 10, marginBottom: 4 },
  content: { padding: 16, paddingBottom: 32 },
  question: {
    fontSize: 16, fontWeight: '700', color: '#fff', textAlign: 'center', marginBottom: 10,
  },
  hint: { fontSize: 11, color: '#aaa', textAlign: 'center', marginBottom: 16 },
  fieldLabel: { fontSize: 12, fontWeight: '600', color: '#aaa', marginBottom: 6, marginTop: 14 },
  fieldInput: {
    backgroundColor: '#2a2a2a', borderWidth: 1, borderColor: '#444',
    borderRadius: 8, padding: 12, fontSize: 14, color: '#fff',
  },
  fieldInputError: { borderColor: '#C8102E' },
  fieldError: { color: '#ff6b6b', fontSize: 11, marginTop: 4, marginBottom: 2 },
  sexRow: { flexDirection: 'row', gap: 8, marginTop: 4 },
  sexBtn: {
    flex: 1, paddingVertical: 10, borderRadius: 8,
    borderWidth: 1.5, borderColor: '#444', alignItems: 'center',
  },
  sexBtnActive: { borderColor: '#C8102E', backgroundColor: 'rgba(200,16,46,0.15)' },
  sexBtnText: { fontSize: 12, fontWeight: '600', color: '#888' },
  sexBtnTextActive: { color: '#C8102E' },
  card: { backgroundColor: '#333', borderRadius: 10, padding: 12, marginBottom: 8 },
  cardSelected: { backgroundColor: '#C8102E' },
  cardLabel: { fontSize: 13, fontWeight: '600', color: '#fff' },
  cardLabelSelected: { fontWeight: '700' },
  cardDesc: { fontSize: 11, color: '#aaa', marginTop: 2 },
  cardDescSelected: { color: 'rgba(255,255,255,0.75)' },
  muscleCard: {
    backgroundColor: '#333', borderRadius: 8, padding: 10,
    marginBottom: 6, flexDirection: 'row', alignItems: 'center',
  },
  muscleCardSelected: { backgroundColor: '#C8102E' },
  checkMark: { fontSize: 16, color: '#fff', marginLeft: 8 },
  tilesRow: { flexDirection: 'row', justifyContent: 'center', gap: 10, marginTop: 8 },
  tile: {
    backgroundColor: '#333', borderRadius: 12, width: 64, height: 72,
    alignItems: 'center', justifyContent: 'center',
  },
  tileSelected: { backgroundColor: '#C8102E' },
  tileNumber: { fontSize: 22, fontWeight: '700', color: '#fff' },
  tileNumberSelected: { color: '#fff' },
  tileSub: { fontSize: 10, color: '#aaa' },
  tileSubSelected: { color: 'rgba(255,255,255,0.8)' },
  textarea: {
    backgroundColor: '#2a2a2a', borderWidth: 1, borderColor: '#444',
    borderRadius: 10, padding: 12, fontSize: 13, color: '#fff', minHeight: 120,
  },
  optional: { fontSize: 10, color: '#555', textAlign: 'center', marginTop: 10 },
  footer: { padding: 16, paddingBottom: 32 },
  nextBtn: { backgroundColor: '#C8102E', borderRadius: 10, padding: 14, alignItems: 'center' },
  nextBtnDisabled: { opacity: 0.5 },
  nextBtnText: { color: '#fff', fontWeight: '700', fontSize: 15 },
  conclusionScreen: {
    flex: 1, backgroundColor: '#1a1a1a', alignItems: 'center',
    justifyContent: 'center', padding: 32,
  },
  conclusionEmoji: { fontSize: 56, marginBottom: 16 },
  conclusionTitle: { fontSize: 22, fontWeight: '700', color: '#fff', marginBottom: 12 },
  conclusionText: {
    fontSize: 14, color: '#aaa', textAlign: 'center', lineHeight: 22, marginBottom: 32,
  },
  conclusionBtn: {
    backgroundColor: '#C8102E', borderRadius: 10, paddingVertical: 14, paddingHorizontal: 32,
  },
  conclusionBtnText: { color: '#fff', fontWeight: '700', fontSize: 15 },
  saveErrorText: { color: '#ff6b6b', fontSize: 12, textAlign: 'center', marginBottom: 8 },
});
